/**
 * Client-lib voor klachten — typed fetch-helpers + error class.
 *
 * Types worden hier ook gedefinieerd (en moeten matchen met
 * `api/klachten/_types.ts`). Reden: tsconfig.app `include` is alleen `src/`,
 * dus directe imports vanuit `../../api/...` worden niet door tsc opgepakt.
 * Bij wijzigingen aan deze types: spiegel ze in `_types.ts`.
 */

import { supabase } from './supabase';
import type { KlachtBehandeling } from './klacht-plan';

export interface KlachtBehandelingData {
  behandelingen: KlachtBehandeling[];
  pauzes: Array<{ id: string; wo_sf_id: string; sinds: string; afleveren: boolean; betalen: boolean; opslag: boolean; reden: string }>;
  enabled: boolean;
  beperkt?: boolean;
}
export function fetchKlachtBehandelingen(klachtId?: string): Promise<KlachtBehandelingData> {
  return getJson(`/api/klachten/behandeling${klachtId ? `?klacht_id=${encodeURIComponent(klachtId)}` : ''}`, 'Klachtbehandelingen ophalen mislukt');
}
export function fetchKlachtBehandeling(behandelingId: string): Promise<KlachtBehandelingData> {
  return getJson(`/api/klachten/behandeling?behandeling_id=${encodeURIComponent(behandelingId)}`, 'Klachtbehandeling ophalen mislukt');
}
/** Zelfde antwoordvorm als de klachtgenerator, zonder runtime-import van servercode. */
export type KlachtConceptResultaat = {
  ok: true;
  messageId: string;
  advies: import('../stores/inbox-store').KlachtAdvies;
  model: string;
  draft: string;
  confidence: number;
  confidence_reason: string;
  images: number;
  imagesSkipped: string[];
  acties: import('./mail-acties').VoorgesteldeActie[];
  actie_context: string | null;
} | { ok: false; error: string; status: number; messageId?: string };
export interface WijzigKlachtBehandelingResultaat {
  ok: boolean;
  behandeling?: KlachtBehandeling;
  /** Een fout hierin betekent dat het oordeel wél is opgeslagen. */
  concept?: KlachtConceptResultaat;
}
export function wijzigKlachtBehandeling(body: Record<string, unknown>): Promise<WijzigKlachtBehandelingResultaat> {
  return postJson('/api/klachten/behandeling', body, 'Klachtbehandeling wijzigen mislukt');
}

// ─── Types (mirror van api/klachten/_types.ts) ───────────────────────────────

export type KlachtStatus =
  | 'Geregistreerd'
  | 'In_Behandeling'
  | 'Wacht_op_Klant'
  | 'Opgelost'
  | 'Gesloten';

export type KlachtBron =
  | 'Office-App'
  | 'Client-App'
  | 'Field-App'
  | 'Partner-Portal'
  | 'Phone-Agent'
  | 'AI-Email'
  | 'Brein'
  | 'Brein-Auto';

export type KlachtUpdateType =
  | 'StatusChange'
  | 'Comment'
  | 'ActionAdded'
  | 'ActionResolved'
  | 'Heropend'
  | 'SystemNote';

export type RefundMethode = 'Mollie' | 'Bank' | 'Coulance_Tegoed' | 'Factuur_Correctie';
export type RefundStatus = 'Pending' | 'Verwerkt' | 'Mislukt';

export interface KlachtSummary {
  betrokkenChauffeur?: import('./klacht-chauffeur').KlachtChauffeur | null;
  id: string;
  klachtNummer: string;
  accountId: string;
  accountName?: string | null;
  originalWerkOrderId: string;
  originalWerkOrderNumber?: string | null;
  brand: string | null;
  status: KlachtStatus | string;
  type: string;
  klantVerwacht?: string | null;
  eigenaarUserId?: string | null;
  eigenaarName?: string | null;
  bron: string;
  datumGeopend: string;
  datumGesloten?: string | null;
  dagenOpen: number;
}

export interface KlachtUpdate {
  id: string;
  type: KlachtUpdateType | string;
  door?: string | null;
  datumTijd: string;
  tekst: string;
  bron: string;
}

export interface RefundSummary {
  id: string;
  refundNummer: string;
  bedragInclBtw: number;
  methode: RefundMethode | string;
  status: RefundStatus | string;
  werkOrderId?: string | null;
  mollieRefundId?: string | null;
  datumVerwerkt?: string | null;
  foutmelding?: string | null;
  reden?: string | null;
}

export interface VervolgWoSummary {
  id: string;
  werkOrderNummer: string;
  status: string;
  afleverdatum?: string | null;
  aantalKleden: number;
}

export interface CoulanceTegoedSummary {
  id: string;
  code: string;
  bedragInclBtw: number;
  resterend: number;
  geldigTot: string | null;
  status: string;
  toelichting?: string | null;
}

export interface KlachtDetail extends KlachtSummary {
  beschrijving: string;
  fotoUrls?: string[];
  redenen?: string[];
  resolutieNotitie?: string | null;
  heropendDoor?: string | null;
  heropendDatum?: string | null;
  updates: KlachtUpdate[];
  refunds: RefundSummary[];
  vervolgWerkOrders: VervolgWoSummary[];
  coulanceTegoeden: CoulanceTegoedSummary[];
  /**
   * Mollie-payment-id (pl_ of tr_) van de originele werkorder. Voedt de
   * refund-dialog zodat methode Mollie beschikbaar is; null = niet via Mollie betaald.
   */
  originalWerkOrderMolliePaymentId?: string | null;
}

export interface RegisterKlachtInput {
  betrokkenChauffeur?: import('./klacht-chauffeur').KlachtChauffeur | null;
  originalWerkOrderId: string;
  type: string;
  redenen?: string[];
  klantVerwacht?: string;
  beschrijving: string;
  fotoUrls?: string[];
  eigenaarUserId?: string;
}

export interface RegisterKlachtResult {
  klachtId: string;
  klachtNummer: string;
  status: string;
  /** True: er stond al een open klacht op de order; de melding is daar als update op gezet. */
  bestaand?: boolean;
}

export interface AddActionInput {
  klachtId: string;
  actionType: string;
  params: Record<string, unknown>;
}

/**
 * Params voor `actionType='NewWerkOrder'`. `factureerbareItemIds` is een
 * subset van `wasserijItemIds` waarvan items op de vervolg-WO tegen
 * catalogprijs gefactureerd worden; items er buiten worden gratis uitgevoerd
 * (100% korting + zichtbare eenheidsprijs op de factuur).
 *
 * - `factureerbareItemIds: []` → alle items gratis (equivalent aan legacy
 *   `kostenloos=true`).
 * - `factureerbareItemIds` omitted → handler valt terug op legacy
 *   `kostenloos`-boolean.
 *
 * `extras` hangt per meegenomen kleed extra behandelingen (Ontgeuren, randen, …)
 * aan de vervolg-WO. Elke behandeling regelt zélf gratis/factureren via `bill`,
 * los van de kleed-keuze — zo kun je een kleed gratis herreinigen én een nieuwe
 * behandeling doorberekenen in één actie. `sourceItemId` moet in
 * `wasserijItemIds` zitten.
 */
export interface NewWerkOrderActionParams {
  wasserijItemIds: string[];
  factureerbareItemIds?: string[];
  /** @deprecated gebruik `factureerbareItemIds` ([] = alles gratis, undefined = legacy). */
  kostenloos?: boolean;
  bevestigingenSturen?: boolean;
  redenVoorVervolg: string;
  extras?: VervolgExtraInput[];
}

export interface AddActionResult {
  actionRecordId: string | null;
  actionType: string;
}

export interface ResolveActionInput {
  klachtId: string;
  actionId: string;
  status: string;
  note?: string;
}

export interface UpdateStatusInput {
  klachtId: string;
  newStatus: string;
  note?: string;
}

export interface CloseKlachtInput {
  klachtId: string;
  resolutie: string;
}

export interface KlachtListFilters {
  status?: 'open' | 'closed' | 'all' | KlachtStatus;
  type?: string;
  eigenaarUserId?: string;
  brand?: string;
  search?: string;
  limit?: number;
}

export interface KlachtCounts {
  open: number;
  total: number;
}

export interface WasserijItemForPicker {
  id: string;
  naam: string;
  lengteCm: number | null;
  breedteCm: number | null;
  diameterCm: number | null;
  oppervlakteM2: number | null;
  verkoopprijsInclBtw: number | null;
  /**
   * Hoofdproduct-Id van het kleed (Product__c). Voedt de extra-behandeling
   * catalog-picker (filtert op Gerelateerd_hoofdproduct__c). Null = geen
   * hoofdproduct → behandeling toevoegen niet mogelijk.
   */
  primaryProductId: string | null;
}

/**
 * Eén extra behandeling op een meegenomen kleed in de vervolg-WO. `sourceItemId`
 * verwijst naar een item uit `wasserijItemIds`; `bill` regelt per stuk
 * gratis/factureren, los van het kleed zelf.
 */
export interface VervolgExtraInput {
  sourceItemId: string;
  productId: string;
  bill: 'free' | 'paid';
  quantity?: number;
}

export interface InvoiceLineForPicker {
  id: string;
  invoiceId: string;
  invoiceName: string;
  productName: string;
  aantal: number;
  prijsInclBtw: number;
  totaalInclBtw: number;
  korting: number;
}

// ─── Error class + auth ──────────────────────────────────────────────────────

export class KlachtError extends Error {
  errorCode?: string;
  status: number;
  constructor(message: string, status = 400, errorCode?: string) {
    super(message);
    this.name = 'KlachtError';
    this.status = status;
    this.errorCode = errorCode;
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  return headers;
}

async function parseError(res: Response, fallback: string): Promise<KlachtError> {
  const body = (await res.json().catch(() => null)) as { error?: string; code?: string } | null;
  return new KlachtError(body?.error || fallback, res.status, body?.code);
}

async function postJson<T>(path: string, body: unknown, fallbackError: string): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await parseError(res, fallbackError);
  return res.json() as Promise<T>;
}

async function patchJson<T>(path: string, body: unknown, fallbackError: string): Promise<T> {
  const res = await fetch(path, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await parseError(res, fallbackError);
  return res.json() as Promise<T>;
}

async function getJson<T>(path: string, fallbackError: string): Promise<T> {
  const res = await fetch(path, { headers: await authHeaders() });
  if (!res.ok) throw await parseError(res, fallbackError);
  return res.json() as Promise<T>;
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function registerKlacht(input: RegisterKlachtInput): Promise<RegisterKlachtResult> {
  return postJson('/api/klachten/register', input, 'Klacht registreren mislukt');
}

export function addKlachtAction(input: AddActionInput): Promise<AddActionResult> {
  return postJson('/api/klachten/add-action', input, 'Action toevoegen mislukt');
}

export interface RefundWithCreditInput {
  klachtId: string;
  werkOrderId: string;
  bedragInclBtw: number;
  methode: 'Mollie' | 'Bank';
  reden: string;
  molliePaymentId?: string | null;
  btwPct?: 0 | 9 | 21;
}

export interface RefundWithCreditResult {
  ok: boolean;
  creditInvoiceId?: string;
  creditInvoiceNaam?: string;
  verrekend?: number;
  terugTeBetalen?: number;
  origineelOpenstaandNa?: number;
  refund?: { actionRecordId: string | null } | null;
  stepErrors?: Array<{ step: string; code?: string; message?: string }>;
}

/**
 * Klacht-refund mét bijbehorende creditfactuur (administratief sluitend).
 * Zie api/klachten/refund-with-credit.ts. Alleen voor Mollie/Bank.
 */
export function refundWithCredit(input: RefundWithCreditInput): Promise<RefundWithCreditResult> {
  return postJson('/api/klachten/refund-with-credit', input, 'Refund + creditfactuur mislukt');
}

export function resolveKlachtAction(input: ResolveActionInput): Promise<{ actionRecordId: string }> {
  return patchJson('/api/klachten/resolve-action', input, 'Action resolve mislukt');
}

export function updateKlachtStatus(
  input: UpdateStatusInput
): Promise<{ oldStatus: string; newStatus: string }> {
  return patchJson('/api/klachten/update-status', input, 'Status-wijziging mislukt');
}

export function closeKlacht(input: CloseKlachtInput): Promise<{ message?: string }> {
  return postJson('/api/klachten/close', input, 'Sluiten mislukt');
}

// ─── Reads ───────────────────────────────────────────────────────────────────

export function fetchKlachtDetail(id: string): Promise<KlachtDetail> {
  return getJson(`/api/klachten/detail?id=${encodeURIComponent(id)}`, 'Klacht ophalen mislukt');
}

export async function fetchKlachtList(
  filters: KlachtListFilters = {}
): Promise<KlachtSummary[]> {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.type) qs.set('type', filters.type);
  if (filters.eigenaarUserId) qs.set('eigenaar', filters.eigenaarUserId);
  if (filters.brand) qs.set('brand', filters.brand);
  if (filters.search) qs.set('search', filters.search);
  if (filters.limit) qs.set('limit', String(filters.limit));
  const result = await getJson<{ klachten: KlachtSummary[] }>(
    `/api/klachten/list?${qs.toString()}`,
    'Klachten lijst ophalen mislukt'
  );
  return result.klachten;
}

export function fetchKlachtCounts(): Promise<KlachtCounts> {
  return getJson<KlachtCounts>('/api/klachten/list?countOnly=1', 'Klacht-count ophalen mislukt');
}

export async function fetchWasserijItemsForVervolg(
  werkOrderId: string
): Promise<WasserijItemForPicker[]> {
  const result = await getJson<{ items: WasserijItemForPicker[] }>(
    `/api/klachten/wasserij-items?werkOrderId=${encodeURIComponent(werkOrderId)}`,
    'Items ophalen mislukt'
  );
  return result.items;
}

export async function fetchInvoiceLinesForAdjust(
  werkOrderId: string
): Promise<{ invoiceId: string; lines: InvoiceLineForPicker[] }> {
  return getJson(
    `/api/klachten/invoice-lines?werkOrderId=${encodeURIComponent(werkOrderId)}`,
    'Factuur-regels ophalen mislukt'
  );
}
